<template>
    <h2 class="font-600 font-md mt-0 w-10/12 font-inter font-semibold md:w-full legacy-md:mt-3">Similar Early Deposit found</h2>
    <p class="relative mb-6 mt-2">An early deposit was found that may match this file. Please review and merge if necessary.</p>
    <h3 class="mb-2 mt-8 font-inter font-semibold">Submitted File</h3>
    <DataTable
        data-key="id"
        :row-class="() => 'bg-ds-blue-100 align-top'"
        :rows="1"
        :value="[file]"
    >
        <Column
            :class="[cellClasses, 'text-balance']"
            field="address"
            header="Property"
            :header-class="headerClasses"
        />
        <Column
            :class="cellClasses"
            field="ref_number"
            header="Ref Number"
            :header-class="headerClasses"
        />
        <Column
            :class="cellClasses"
            field="balance_due"
            header="Amount Due"
            :header-class="headerClasses"
        >
            <template #body="{ data: fileData }">
                <div :class="{ [placeholderClasses]: !fileData.balance_due }">{{ fileData.balance_due || '--' }}</div>
            </template>
        </Column>
        <Column
            :class="[cellClasses, 'text-balance']"
            field="company_branch"
            header="Company/Branch"
            :header-class="headerClasses"
        >
            <template #body> {{ companyOnFile }}, {{ branchOnFile }} </template>
        </Column>
        <Column
            :class="cellClasses"
            field="agents"
            header="Real Estate Agent"
            :header-class="headerClasses"
        >
            <template #body>
                <span
                    v-if="!agentsOnFile.length"
                    :class="placeholderClasses"
                    >--</span
                >
                <template v-else>
                    <div
                        v-for="agent in agentsOnFile"
                        :key="agent.email"
                    >
                        {{ agent.email }}
                    </div>
                </template>
            </template>
        </Column>
        <Column
            :class="cellClasses"
            field="buyers"
            header="Buyer"
            :header-class="headerClasses"
        >
            <template #body>
                <span
                    v-if="!buyersOnFile.length"
                    :class="placeholderClasses"
                    >--</span
                >
                <template v-else>
                    <div
                        v-for="buyer in buyersOnFile"
                        :key="buyer.email"
                    >
                        {{ buyer.email }}
                    </div>
                </template>
            </template>
        </Column>
    </DataTable>

    <h3 class="mb-2 mt-12 font-inter font-semibold">Early Deposits</h3>
    <DataTable
        data-key="id"
        :row-class="() => 'align-top hover:bg-ds-blue-100 transition-colors'"
        :value="similarEmds"
        @row-click="
            ({ data: emdData }: Record<'data', EarlyDeposit>) => {
                selectedEmd = emdData;
            }
        "
    >
        <Column
            :class="[cellClasses, 'text-balance']"
            field="address"
            header="Property"
            :header-class="headerClasses"
        />
        <Column
            :class="[cellClasses, 'text-balance']"
            field="company_branch"
            header="Company/Branch"
            :header-class="headerClasses"
        >
            <template #body="{ data: emdData }: { data: EarlyDeposit }">
                {{ emdData.branch?.title?.name }}, {{ emdData.branch?.name }}
            </template>
        </Column>
        <Column
            :class="cellClasses"
            field="agents"
            header="Real Estate Agent"
            :header-class="headerClasses"
        >
            <template #body="{ data: emdData }: { data: EarlyDeposit }">
                <span
                    v-if="!emdIdToClientsMap[emdData.id].agents.length"
                    :class="placeholderClasses"
                    >--</span
                >
                <template v-else>
                    <div
                        v-for="agent in emdIdToClientsMap[emdData.id].agents"
                        :key="agent.email"
                    >
                        {{ agent.email }}
                    </div>
                </template>
            </template>
        </Column>
        <Column
            :class="cellClasses"
            field="buyers"
            header="Buyer"
            :header-class="headerClasses"
        >
            <template #body="{ data: emdData }: { data: EarlyDeposit }">
                <span
                    v-if="!emdIdToClientsMap[emdData.id].buyers.length"
                    :class="placeholderClasses"
                    >--</span
                >
                <template v-else>
                    <div
                        v-for="buyer in emdIdToClientsMap[emdData.id].buyers"
                        :key="buyer.email"
                    >
                        {{ buyer.email }}
                    </div>
                </template>
            </template>
        </Column>
        <Column
            :class="cellClasses"
            field="select"
            header="Select"
            :header-class="headerClasses"
        >
            <template #body="{ data: emdData }: { data: EarlyDeposit }">
                <input
                    :checked="selectedEmd?.id === emdData.id"
                    class="ml-2 h-5 w-5 align-middle"
                    name="emd-to-merge"
                    type="radio"
                    :value="emdData"
                    @click="selectedEmd = emdData"
                />
            </template>
        </Column>
    </DataTable>

    <footer class="mt-4 grid gap-y-2 md:mt-[max(1.5rem,5vw)] md:flex md:justify-end md:gap-x-4">
        <DsButton
            size="md"
            variant="secondary"
            @click="closeAndUnmount"
        >
            Cancel
        </DsButton>
        <span class="flex-1"></span>
        <DsButton
            size="md"
            variant="secondary"
            @click="emit('confirmNoMerge')"
        >
            Create New File
        </DsButton>
        <DsButton
            :disabled="!selectedEmd"
            size="md"
            variant="primary"
            @click="goToMerge"
        >
            Continue with Merge
        </DsButton>
    </footer>
</template>

<script setup lang="ts">
import type { EarlyDeposit } from '@/api/interfaces/early-deposit.types';
import type { SimilarFilesProps } from '@/company-portal/add-view-file';
import {
    convertFormDataToCreateFileRequest,
    getEmdClientsOfRole,
    mapCompanyBranchIdsToNames,
    zipClientsFromFormArrays,
} from '@/company-portal/add-view-file';
import DsButton from '@ds/button/DsButton.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { computed, ref } from 'vue';

type ClientObject = ReturnType<typeof zipClientsFromFormArrays>[number];
type ClientsOnEmdByRole = {
    [K: EarlyDeposit['id']]: Record<'agents' | 'buyers', EarlyDeposit['clients']>;
};

const props = defineProps<
    SimilarFilesProps & {
        closeAndUnmount: () => void;
    }
>();

const [selectedEmd] = defineModel<EarlyDeposit>({
    set: newEmd => {
        if (newEmd.id === selectedEmd.value?.id) return;
        return newEmd;
    },
});

const emit = defineEmits<{ confirmNoMerge: []; nextStep: [] }>();

const { company: companyOnFile, branch: branchOnFile } = mapCompanyBranchIdsToNames();

const dialogRef = ref<HTMLDialogElement>();
const mergeDialogRef = ref<HTMLDialogElement>();
const file = ref(convertFormDataToCreateFileRequest(props.fullFileFormData));
const fileClients = ref<ClientObject[]>(props.fullFileFormData ? zipClientsFromFormArrays(props.fullFileFormData) : []);
const agentsOnFile = computed(() => fileClients.value.filter(client => client.role === 'agent'));
const buyersOnFile = computed(() => fileClients.value.filter(client => client.role === 'buyer'));
const emdIdToClientsMap = computed<ClientsOnEmdByRole>(() => {
    return props.similarEmds.reduce<ClientsOnEmdByRole>((map, emd) => {
        map[emd.id] = {
            agents: getEmdClientsOfRole(emd.clients, 'agent'),
            buyers: getEmdClientsOfRole(emd.clients, 'buyer'),
        };
        return map;
    }, {});
});

const cellClasses = 'p-3 border-b border-ds-primary-300';
const headerClasses = 'border-ds-primary-500';
const placeholderClasses = 'pl-[min(1vw,1.5rem)] text-nowrap font-inter';

const goToMerge = () => {
    dialogRef.value?.close();
    mergeDialogRef.value?.showModal();
    emit('nextStep');
};
</script>

<style scoped>
:deep(tr:has(:checked)) {
    @apply bg-ds-blue-100;
}
</style>
